import {
  Team,
  BracketMatch,
  BracketRound,
  SingleEliminationTournament,
} from "../types/tournament";
import { generateId, createGameResult } from "./tournamentLogic";

// Generate round names based on number of teams
export const getRoundName = (
  roundNumber: number,
  totalTeams: number
): string => {
  const roundNames = [
    "Championship",
    "Semifinals",
    "Quarterfinals",
    "Round of 8",
    "Round of 16",
    "Round of 32",
    "Round of 64",
    "Round of 128",
  ];

  const totalRounds = Math.ceil(Math.log2(totalTeams));
  const roundIndex = totalRounds - roundNumber;

  return roundNames[roundIndex] || `Round ${roundNumber}`;
};

// Calculate total rounds needed for tournament
export const calculateTotalRounds = (teamCount: number): number => {
  return Math.ceil(Math.log2(teamCount));
};

// Generate initial bracket with random seeding
export const generateBracket = (teams: Team[]): BracketRound[] => {
  const shuffledTeams = [...teams].sort(() => Math.random() - 0.5);
  const totalRounds = calculateTotalRounds(teams.length);
  const rounds: BracketRound[] = [];

  // Calculate matches needed for first round
  const firstRoundMatchCount = Math.pow(2, totalRounds - 1);

  // Create first round matches
  const firstRoundMatches: BracketMatch[] = [];
  let teamIndex = 0;

  for (let i = 0; i < firstRoundMatchCount; i++) {
    const matchNumber = i + 1;
    const team1 =
      teamIndex < shuffledTeams.length ? shuffledTeams[teamIndex] : null;
    const team2 =
      teamIndex + 1 < shuffledTeams.length
        ? shuffledTeams[teamIndex + 1]
        : null;

    const match: BracketMatch = {
      id: generateId(),
      round: 1,
      matchNumber,
      team1,
      team2,
      winner: null,
      loser: null,
      result: null,
      nextMatchId: null,
      isBye: !team1 || !team2,
      isCompleted: false,
    };

    firstRoundMatches.push(match);
    teamIndex += 2;
  }

  // Handle byes - advance teams automatically
  firstRoundMatches.forEach((match) => {
    if (match.isBye) {
      match.winner = match.team1 || match.team2;
      match.isCompleted = true;
    }
  });

  rounds.push({
    roundNumber: 1,
    roundName: getRoundName(1, teams.length),
    matches: firstRoundMatches,
    isCompleted: false,
  });

  // Generate subsequent rounds
  for (let round = 2; round <= totalRounds; round++) {
    const matchesInRound = Math.pow(2, totalRounds - round);
    const roundMatches: BracketMatch[] = [];

    for (let i = 0; i < matchesInRound; i++) {
      const matchNumber = i + 1;
      const match: BracketMatch = {
        id: generateId(),
        round,
        matchNumber,
        team1: null,
        team2: null,
        winner: null,
        loser: null,
        result: null,
        nextMatchId: null,
        isBye: false,
        isCompleted: false,
      };

      roundMatches.push(match);
    }

    rounds.push({
      roundNumber: round,
      roundName: getRoundName(round, teams.length),
      matches: roundMatches,
      isCompleted: false,
    });
  }

  // Link matches to next round
  for (let round = 0; round < rounds.length - 1; round++) {
    const currentRound = rounds[round];
    const nextRound = rounds[round + 1];

    currentRound.matches.forEach((match, index) => {
      const nextMatchIndex = Math.floor(index / 2);
      if (nextMatchIndex < nextRound.matches.length) {
        match.nextMatchId = nextRound.matches[nextMatchIndex].id;
      }
    });
  }

  return rounds;
};

// Update match result and advance winner
export const updateBracketMatch = (
  tournament: SingleEliminationTournament,
  matchId: string,
  team1Score: number,
  team2Score: number
): SingleEliminationTournament => {
  const result = createGameResult(team1Score, team2Score, "", "");
  const winnerId = result.winner;
  const loserId = result.loser;

  // Find the match
  let targetMatch: BracketMatch | null = null;
  let targetRoundIndex = -1;
  let targetMatchIndex = -1;

  for (
    let roundIndex = 0;
    roundIndex < tournament.rounds.length;
    roundIndex++
  ) {
    const round = tournament.rounds[roundIndex];
    for (let matchIndex = 0; matchIndex < round.matches.length; matchIndex++) {
      const match = round.matches[matchIndex];
      if (match.id === matchId) {
        targetMatch = match;
        targetRoundIndex = roundIndex;
        targetMatchIndex = matchIndex;
        break;
      }
    }
    if (targetMatch) break;
  }

  if (!targetMatch) return tournament;

  // Update match result
  const updatedMatch: BracketMatch = {
    ...targetMatch,
    result,
    winner:
      winnerId === targetMatch.team1?.id
        ? targetMatch.team1
        : targetMatch.team2,
    loser:
      loserId === targetMatch.team1?.id ? targetMatch.team1 : targetMatch.team2,
    isCompleted: true,
  };

  // Update teams stats
  const updatedTeams = tournament.teams.map((team) => {
    if (team.id === winnerId) {
      return { ...team, wins: team.wins + 1 };
    }
    if (team.id === loserId) {
      return { ...team, losses: team.losses + 1 };
    }
    return team;
  });

  // Update rounds
  const updatedRounds = [...tournament.rounds];
  updatedRounds[targetRoundIndex] = {
    ...updatedRounds[targetRoundIndex],
    matches: updatedRounds[targetRoundIndex].matches.map((match, index) =>
      index === targetMatchIndex ? updatedMatch : match
    ),
  };

  // Advance winner to next round
  if (updatedMatch.nextMatchId && updatedMatch.winner) {
    for (let roundIndex = 0; roundIndex < updatedRounds.length; roundIndex++) {
      const round = updatedRounds[roundIndex];
      for (
        let matchIndex = 0;
        matchIndex < round.matches.length;
        matchIndex++
      ) {
        const match = round.matches[matchIndex];
        if (match.id === updatedMatch.nextMatchId) {
          // Determine if this is team1 or team2 slot based on match number
          const previousMatchNumber = updatedMatch.matchNumber;
          const isTeam1Slot = previousMatchNumber % 2 === 1;

          const updatedNextMatch: BracketMatch = {
            ...match,
            team1: isTeam1Slot ? updatedMatch.winner : match.team1,
            team2: isTeam1Slot ? match.team2 : updatedMatch.winner,
          };

          updatedRounds[roundIndex] = {
            ...round,
            matches: round.matches.map((m, index) =>
              index === matchIndex ? updatedNextMatch : m
            ),
          };
          break;
        }
      }
    }
  }

  // Check if round is complete
  const currentRound = updatedRounds[targetRoundIndex];
  const isRoundComplete = currentRound.matches.every(
    (match) => match.isCompleted
  );

  if (isRoundComplete) {
    updatedRounds[targetRoundIndex] = {
      ...currentRound,
      isCompleted: true,
    };
  }

  // Check if tournament is complete
  const isTournamentComplete = updatedRounds.every(
    (round) => round.isCompleted
  );
  const champion = isTournamentComplete
    ? updatedMatch.winner
    : tournament.champion;
  const runnerUp = isTournamentComplete
    ? updatedMatch.loser
    : tournament.runnerUp;

  return {
    ...tournament,
    teams: updatedTeams,
    rounds: updatedRounds,
    currentRound: isRoundComplete
      ? tournament.currentRound + 1
      : tournament.currentRound,
    champion,
    runnerUp,
    updatedAt: new Date(),
  };
};

// Get current active matches
export const getCurrentMatches = (
  tournament: SingleEliminationTournament
): BracketMatch[] => {
  const currentRound = tournament.rounds.find(
    (round) => round.roundNumber === tournament.currentRound
  );
  return currentRound?.matches.filter((match) => !match.isCompleted) || [];
};

// Get tournament progress
export const getTournamentProgress = (
  tournament: SingleEliminationTournament
) => {
  const totalMatches = tournament.rounds.reduce(
    (total, round) => total + round.matches.length,
    0
  );
  const completedMatches = tournament.rounds.reduce(
    (total, round) =>
      total + round.matches.filter((match) => match.isCompleted).length,
    0
  );

  return {
    totalMatches,
    completedMatches,
    progress: (completedMatches / totalMatches) * 100,
  };
};

// Create a new single elimination tournament
export const createSingleEliminationTournament = (
  name: string,
  teams: Team[],
  totalRounds?: number
): SingleEliminationTournament => {
  const rounds = generateBracket(teams);
  const calculatedTotalRounds = totalRounds || rounds.length;

  return {
    id: generateId(),
    name,
    teams,
    rounds,
    currentRound: 1,
    totalRounds: calculatedTotalRounds,
    playerConfig: {
      playersPerTeam: 2,
      minTeamsRequired: 2,
      maxTeamsAllowed: 64,
      totalPlayersExpected: teams.length * 2,
      allowSubstitutes: false,
      maxSubstitutesPerTeam: 0,
    },
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    liveDashboardEnabled: true,
    qrCodeUrl: "",
    tournamentType: "single_elimination",
    champion: null,
    runnerUp: null,
  };
};
